/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import numeral from 'numeral';
import { borderWidth, padding, tint } from 'polished';
import { pure } from 'recompose';

import PlayerAvatar from 'app/components/challenge/PlayerAvatar';
import PlayerBar from 'app/components/challenge/PlayerBar';
import PlayerDatum from 'app/components/challenge/PlayerDatum';
import Responsive from 'app/components/responsive/Responsive';
import Tappable from 'app/components/commons/Tappable';
import StatusIcon from 'app/components/challenge/StatusIcon';
import StarSymbol from '../challenge/StarSymbol';
import PlayerCell from './PlayerCell';
import { isInProgress } from '../../utils/challengeUtils';

type Props = {
  average?: number,
  awayTeam?: string,
  gameOrder?: number,
  homeTeam?: string,
  form?: number | string,
  image?: string,
  initials?: string,
  isHome: boolean,
  isRacing: boolean,
  isScratched?: boolean,
  labels: Array<*>,
  name: string,
  onClick: () => void,
  place?: number,
  position?: string,
  reverseOrder?: boolean,
  rider?: string,
  riderImage?: string,
  salary: number,
  selected?: boolean,
  statusId: number,
  trainer?: string,
  win?: number
};

const makeRowBackground = col =>
  `linear-gradient(to right, transparent 0, transparent 2rem, ${col} 2rem, ${col} 100%)`;

const defaultProps = {
  awayTeam: '',
  average: undefined,
  form: undefined,
  gameOrder: undefined,
  homeTeam: '',
  image: undefined,
  initials: undefined,
  isScratched: false,
  place: undefined,
  position: undefined,
  reverseOrder: false,
  rider: undefined,
  riderImage: undefined,
  selected: false,
  trainer: undefined,
  win: undefined
};

const Row = styled.div(({ theme }) => ({
  alignItems: 'stretch',
  backgroundColor: 'white',
  borderColor: theme.color.basis5,
  borderStyle: 'solid',
  ...borderWidth(0, 0, 1),
  display: 'flex',
  minWidth: 0,
  ...padding('0.5em', 0)
}));

const TableRow = styled.div(
  {
    display: 'table-row'
  },
  ({ selected, theme }) =>
    selected
      ? {
          background: theme.color.secondary,
          color: 'white'
        }
      : {
          background: 'white',
          '&:hover': {
            background: tint(0.25, theme.color.secondary)
          },
          [theme.mediaQuery.desktopAndUp]: {
            background: makeRowBackground('white'),
            '&:hover': {
              background: makeRowBackground(tint(0.25, theme.color.secondary))
            }
          }
        }
);

const Initial = styled.div(({ theme }) => ({
  display: 'none',
  height: '100%',
  textAlign: 'center',
  verticalAlign: 'middle',
  ...borderWidth(0, 0),
  width: '2rem',
  [theme.mediaQuery.desktopAndUp]: {
    display: 'table-cell'
  }
}));

const Cell = styled.div({
  display: 'flex',
  flexBasis: 'auto',
  flexDirection: 'column',
  flexGrow: 0,
  flexShrink: 0,
  justifyContent: 'center',
  ...padding(0, 5)
});

const AvatarCell = styled(PlayerCell)({
  position: 'relative'
});

const MainCell = styled.div({
  display: 'flex',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1
});

const PlayerData = styled.div({
  display: 'flex',
  marginRight: '-0.5em'
});

const Arrow = styled.div({
  alignItems: 'center',
  display: 'flex',
  fontSize: '2rem',
  justifyContent: 'center'
});

const ArrowCircle = styled.span(({ theme }) => ({
  alignItems: 'center',
  color: theme.color.secondary,
  display: 'flex',
  fontSize: '2rem',
  justifyContent: 'center',
  ...padding(0, 5)
}));

const Salary = styled.div(({ theme }) => ({
  fontWeight: theme.fontWeight.medium,
  textAlign: 'right'
}));

const StatusIconWrapper = styled.div(({ live, theme }) => ({
  backgroundImage: `linear-gradient(135deg, ${
    live ? theme.color.primary : theme.color.basis1
  } 50%, transparent 50%)`,
  color: 'white',
  fontSize: '0.5rem',
  height: '2.2em',
  left: 0,
  padding: 1,
  transform: 'rotate(360deg)',
  position: 'absolute',
  top: 0,
  width: '2.2em'
}));

function PlayerPoolRow({
  average,
  awayTeam,
  form,
  gameOrder,
  homeTeam,
  image,
  initials,
  isHome,
  isRacing,
  isScratched,
  labels,
  name,
  onClick,
  place,
  position,
  reverseOrder,
  rider,
  riderImage,
  salary,
  selected,
  statusId,
  trainer,
  win
}: Props) {
  const avatarImage = isRacing ? riderImage : image;

  return (
    <Responsive tabletLandscape desktop>
      {isLargeScreen =>
        isLargeScreen ? (
          <Tappable component={TableRow} onTap={onClick} selected={selected}>
            <Initial>{selected && <Arrow className="ion-ios-arrow-back" />}</Initial>
            <AvatarCell bare>
              <StatusIconWrapper live={isInProgress(statusId)}>
                <StatusIcon statusId={statusId} />
              </StatusIconWrapper>
              <PlayerAvatar fallbackText={initials} image={avatarImage} />
            </AvatarCell>
            <PlayerCell bare>
              <PlayerBar
                awayTeam={awayTeam}
                gameOrder={gameOrder}
                homeTeam={homeTeam}
                labels={labels}
                isHome={isHome}
                isRacing={isRacing}
                isScratched={isScratched}
                name={name}
                place={place}
                position={position}
                reverseOrder={reverseOrder}
                rider={rider}
                trainer={trainer}
                win={win}
              />
            </PlayerCell>
            {isRacing ? (
              <React.Fragment>
                <PlayerCell style={{ width: '140px' }} />
                <PlayerCell>
                  <span>{numeral(win).format('0.00')}</span>
                  <StarSymbol />
                </PlayerCell>
                <PlayerCell>
                  <span>{numeral(place).format('0.00')}</span>
                  <StarSymbol />
                </PlayerCell>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <PlayerCell>{numeral(average).format('0.0')}</PlayerCell>
                <PlayerCell>{numeral(form).format('0.0')}</PlayerCell>
                <PlayerCell bold>{numeral(salary).format('$0,0')}</PlayerCell>
              </React.Fragment>
            )}
          </Tappable>
        ) : (
          <Row>
            <Cell>
              <PlayerAvatar fallbackText={initials} image={avatarImage} />
            </Cell>
            <MainCell>
              <PlayerBar
                awayTeam={awayTeam}
                homeTeam={homeTeam}
                gameOrder={gameOrder}
                isHome={isHome}
                isRacing={isRacing}
                labels={labels}
                name={name}
                place={place}
                position={position}
                reverseOrder={reverseOrder}
                rider={rider}
                trainer={trainer}
                win={win}
              />
            </MainCell>
            <Cell>
              {!isRacing && <Salary>{numeral(salary).format('$0,0')}</Salary>}
              {isRacing ? (
                <PlayerData>
                  <PlayerDatum label="Win">
                    {win ? numeral(win).format('0.00') : '\u2012'}
                  </PlayerDatum>
                  <PlayerDatum label="Place">
                    {place ? numeral(place).format('0.00') : '\u2012'}
                  </PlayerDatum>
                </PlayerData>
              ) : (
                <PlayerData>
                  <PlayerDatum label="Avg">
                    {average ? numeral(average).format('0.0') : '\u2012'}
                  </PlayerDatum>
                  <PlayerDatum label="Form">
                    {form ? numeral(form).format('0.0') : '\u2012'}
                  </PlayerDatum>
                </PlayerData>
              )}
            </Cell>
            <Tappable component={Cell} onTap={onClick}>
              <ArrowCircle className="ion-android-arrow-dropleft-circle" />
            </Tappable>
          </Row>
        )
      }
    </Responsive>
  );
}

PlayerPoolRow.defaultProps = defaultProps;

export default pure(PlayerPoolRow);
